import { getCircleStyle, getFillStrokeProps } from './maputils';
import { getCenter } from 'ol/extent';
import Point from 'ol/geom/Point';
import { Style, Icon } from 'ol/style.js';

const createHalfHalfCircleCanvas = (size, leftColor, rightColor, strokeColor, strokeWidth) => {
    // Create canvas element
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Draw the left half
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, Math.PI / 2, (3 * Math.PI) / 2);
    ctx.lineTo(size / 2, 0);
    ctx.closePath();
    ctx.fillStyle = leftColor;
    ctx.fill();

    // Draw the right half
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, (3 * Math.PI) / 2, Math.PI / 2);
    ctx.lineTo(size / 2, size);
    ctx.closePath();
    ctx.fillStyle = rightColor;
    ctx.fill();

    return canvas;
};

const createStripePattern = (color1, color2, size) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Fill background with first color
    ctx.fillStyle = color1;
    ctx.fillRect(0, 0, size, size);

    // Draw diagonal stripe
    ctx.strokeStyle = color2;
    ctx.lineWidth = size / 3; // Adjust thickness
    ctx.lineCap = "square";

    ctx.beginPath();
    ctx.moveTo(0, size / 2);
    ctx.lineTo(size / 2, 0);
    ctx.stroke();
    ctx.closePath();

    ctx.beginPath();
    ctx.moveTo(size / 2, size);
    ctx.lineTo(size, size / 2);
    ctx.stroke();
    ctx.closePath();

    return ctx.createPattern(canvas, 'repeat');
};

const waterTypeColors = {
    "sāļūdens": { color: '#459f0d' },
    "saldūdens": { color: '#0b87ae' },
    "sālsūdens": { color: '#fc81db' },
    "hlorīdu saldūdens": { color: '#fbff85' },
    "sulfātu iesāļūdens": { color: '#f8b118' },
    "sulfātu saldūdens": { color: '#d64d07' },
    "hlorīdu iesāļūdens": { color: '#d2f487' },
    "unknown": { color: '#999999' }
}
waterTypeColors["saldūdens, sulfātu saldūdens"] = {
    ...waterTypeColors["saldūdens"],
    color2: waterTypeColors["sulfātu saldūdens"].color
}

const createUrbumuAtradnesStyleFunc = (type) => {
    const resolutionThreshold = 600;
    const cachedCircles = {}
    const cachedIcons = {}
    const cachedOuterCircles = {}
    const blackDot = getCircleStyle({
        color: "#333333",
        radius: 1
    })

    //return style function
    return (feature, resolution) => {
        const selected = feature.get('selected')
        const krajspek = feature.get('krajspek')
        const waterType = feature.get("pazemes_udenu_veidi") || "unknown";
        const typeColors = krajspek === false ? waterTypeColors["unknown"] : waterTypeColors[waterType]
        const color = selected === true ? typeColors.color + "FF" : typeColors.color + "DD"
        const fillColor = selected === true ? typeColors.color + "BB" : typeColors.color + "88"
        let color2 = null
        let fillColor2 = null
        if (typeColors.color2) {
            color2 = selected === true ? typeColors.color2 + "FF" : typeColors.color2 + "DD"
            fillColor2 = selected === true ? typeColors.color2 + "BB" : typeColors.color2 + "88"
        }
        const strokeWidth = selected === true ? 1.6 : 1
        const radius = selected === true ? 5 : 4
        const strokeColor = selected === true ? "#ff0000" : "#333333"
        const geomPoly = feature.get('geom_poly')
        const geomPoints = feature.get('geom_points')


        let image;
        if (color2) {
            const key = `${color}_${color2}_${radius}`
            if (!cachedIcons[key]) {
                const size = radius * 2
                cachedIcons[key] = new Icon({
                    img: createHalfHalfCircleCanvas(size, color, color2),
                    imgSize: [size, size],
                })
            }
            image = cachedIcons[key];
        } else {
            const key = `${color}_${radius}`
            if (!cachedCircles[key]) {
                cachedCircles[key] = getCircleStyle({
                    color,
                    radius,
                    strokeWidth: 0,
                    strokeColor: '#ffffff00'
                })
            }
            image = cachedCircles[key];
        }

        const key = `${strokeColor}_${strokeWidth}_${radius}`
        if (!cachedOuterCircles[key]) {
            cachedOuterCircles[key] = getCircleStyle({
                color: '#ffffff00',
                radius: radius + 0.5,
                strokeWidth,
                strokeColor
            })
        }

        if (type === "saraksts") {
            if (resolution > resolutionThreshold) {
                // show just center point
                const centerPoint = new Point(getCenter(feature.getGeometry().getExtent()))
                return [new Style({
                    image: cachedOuterCircles[key],
                    geometry: centerPoint
                }), new Style({
                    image,
                    geometry: centerPoint
                }), new Style({
                    image: blackDot,
                    geometry: centerPoint
                })]
            } else {
                //show both geom_poly and default multipoint
                const styles = [
                    new Style({
                        image: cachedOuterCircles[key],
                        geometry: geomPoints
                    }), new Style({
                        image,
                        geometry: geomPoints
                    }), new Style({
                        image: blackDot,
                        geometry: geomPoints
                    })
                ]
                if (geomPoly) {
                    styles.push(new Style({
                        ...getFillStrokeProps({
                            color: fillColor2 ? createStripePattern(fillColor, fillColor2, 20) : fillColor,
                            strokeColor,
                            strokeWidth
                        }),
                        geometry: geomPoly
                    }))
                }
                return styles
            }
        } else if (type === "poly") {
            return new Style({
                ...getFillStrokeProps({
                    color: fillColor2 ? createStripePattern(fillColor, fillColor2, 20) : fillColor,
                    strokeColor,
                    strokeWidth
                })
            })
        } else {
            return [
                new Style({
                    image: cachedOuterCircles[key]
                }), new Style({
                    image
                }), new Style({
                    image: blackDot
                })
            ]
        }

    }
};

export const urbumuAtradnesSarakstsStyle = {
    default: createUrbumuAtradnesStyleFunc("saraksts"),
    select: {},
    over: {}
}

export const urbumuAtradnesUrbumiStyle = {
    default: createUrbumuAtradnesStyleFunc("points"),
    select: {},
    over: {}
}

export const urbumuAtradnesPolyStyles = {
    default: createUrbumuAtradnesStyleFunc("poly"),
    select: {},
    over: {}
}

export const urbumuAtradnesLegend = {
    features: [
        {
            label: 'Sulfātu iesāļūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'sulfātu iesāļūdens'
        },
        {
            label: 'Hlorīdu iesāļūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'hlorīdu iesāļūdens'
        },
        {
            label: 'Sāļūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'sāļūdens'
        },
        {
            label: 'Sālsūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'sālsūdens'
        },
        {
            label: 'Saldūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'saldūdens'
        },
        {
            label: 'Sulfātu saldūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'sulfātu saldūdens'
        },
        {
            label: 'Saldūdens un sulfātu saldūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'saldūdens, sulfātu saldūdens'
        },
        {
            label: 'Hlorīdu saldūdens atradne',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'hlorīdu saldūdens'
        },
        {
            label: 'Atradne, kurai krājumi nav spēkā',
            geomType: 'point',
            selected: false,
            pazemes_udenu_veidi: 'unknown'
        }
    ]
}
